import React, { useEffect, useState } from 'react';
import Styles from './styles.module.css';
import { getFirestore, doc, getDoc, getDocs, collection, query, where, or } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RxCross2 } from 'react-icons/rx';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { MdMenuBook } from 'react-icons/md';
import { HiNewspaper } from 'react-icons/hi2';
import DefaultImage from '../../image/default/account.png';
import CompanyDefaultImage from '../../image/default/home.png';
import SchoolJobPostings from '../2.students/1.list/userInfo/2.jobPostings/index';

type GuidanceEntry = {
  companyId: string;
  name: string;
  recruitId: string;
  status: string; // '未選択' | '第1志望群' | '第2志望群' | 順位付けの学校は '1位'〜'8位'
  schedule?: string;
  createdAt?: any;
};

type UserRow = {
  uid: string;
  name: string;
  class: string;
  // 出席番号(users.education.number)。Web回答一覧(ログインせず回答した分)は出席番号を
  // 持たないため常に空文字になる。
  number: string;
  answered: boolean;
  reported: boolean;
  group1: GuidanceEntry[];
  group2: GuidanceEntry[];
  // 順位付けの学校: ranked[i] が (i+1)位のサロン（無ければ undefined）
  ranked: (GuidanceEntry | undefined)[];
  all: GuidanceEntry[];
};

// Web のガイダンス(src/pages/guidance)の回答。functions/webGuidance.js の getWebGuidanceAnswers が、
// ログイン中の学校と同じ学校名の分だけ返す(Web は学生がログインしないので、users ではなく guidance コレクションにある)。
type WebGuidanceRow = {
  id: string;
  name: string;
  grade: string;
  className: string;
  course: string;
  reported: boolean;
  answered: boolean;
  entries: GuidanceEntry[];
};

// 「3位」→ 3。順位でなければ null。
const rankOf = (status?: string): number | null => {
  const m = /^(\d+)位$/.exec(status ?? '');
  return m ? Number(m[1]) : null;
};

// 一覧の並び順(行動が早い順): 未回答 → 内定済(報告あり) → 回答済(報告なし)
const statusRank = (r: { answered: boolean; reported: boolean }): number =>
  !r.answered ? 0 : r.reported ? 1 : 2;

// 一覧表の行の中のハイライト色(第1志望群=緑・第2志望群=黄)。淡い色のまま
const GROUP1_COLOR = '#EFF8F3';
const GROUP2_COLOR = '#FFFBEA';

// 詳細モーダルの「第1志望群」「第2志望群」見出しの色。一覧表より、はっきり濃い色にし、
// 文字は黒だと沈んで見えるので白にする
const MODAL_GROUP1_COLOR = '#1F7A52';
const MODAL_GROUP2_COLOR = '#C79A00';
const MODAL_HEAD_TEXT_COLOR = '#FFFFFF';

type GuidancePageProps = {
  selectedClass?: string;
};

const GuidancePage: React.FC<GuidancePageProps> = ({ selectedClass = '' }) => {
  const db = getFirestore();
  const auth = getAuth();

  const [schoolName, setSchoolName] = useState('');
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Web 回答一覧(uid は "web:" + guidance のドキュメントID。アプリの学生の uid と混ざらないように)
  const [webRows, setWebRows] = useState<UserRow[]>([]);
  const [webLoading, setWebLoading] = useState(true);

  const [selectedUid, setSelectedUid] = useState('');
  const [salonDetails, setSalonDetails] = useState<Record<string, any>>({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [isJobPosting, setIsJobPosting] = useState('');
  // 回答方法。info/guidance の学校のエントリが mode: 'rank' なら、志望群ではなく 1位〜rankCount位 の順位付け
  // （学生アプリ guidance/guidance_config.dart と同じ設定。例: ユニバーサル美容専門学校は8位まで）。
  const [rankCount, setRankCount] = useState(0);
  const rankMode = rankCount > 0;

  // 一覧の並び替え(/sanpou の学生一覧と同じ方式: 列見出しをクリックするたびに
  // 昇順⇄降順を切り替える)。既定は出席番号の昇順。
  const [sortConfig, setSortConfig] = useState<{ key: 'number' | 'status'; dir: 'asc' | 'desc' }>({ key: 'number', dir: 'asc' });
  const toggleSort = (key: 'number' | 'status') => {
    setSortConfig(prev => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };
  // 出席番号は文字列(空欄・非数字もある)。数値にできないもの(Web回答一覧は常にこれ)は
  // 昇順・降順どちらでも常に末尾に回す
  const numOrNull = (v: string): number | null => (v === '' || Number.isNaN(Number(v)) ? null : Number(v));
  const sortRows = (list: UserRow[]): UserRow[] => {
    const sorted = [...list].sort((a, b) => {
      if (sortConfig.key === 'status') {
        const diff = statusRank(a) - statusRank(b);
        return sortConfig.dir === 'asc' ? diff : -diff;
      }
      const an = numOrNull(a.number);
      const bn = numOrNull(b.number);
      if (an === null && bn === null) return 0;
      if (an === null) return 1;
      if (bn === null) return -1;
      return sortConfig.dir === 'asc' ? an - bn : bn - an;
    });
    return sorted;
  };

  // 順位付けモードの列(第N希望)は常に rankCount 列分並べるため、学校によって
  // 6列を超える(例: ユニバーサル美容専門学校は8列)と、1列あたり既定の幅(120px)のままでは
  // テーブル内側の使用可能幅(1150px = table幅1200px - 左右padding25px*2)から
  // 右側の固定部分(写真39+名前170+番号65+回答65+詳細65=404px)を引いた幅に収まらず、
  // 右端の「詳細」ボタンがテーブル外にはみ出る。列数に応じて1列あたりの幅を
  // 縮め、6列以下(既定)のときは従来どおりの120pxのまま変わらないようにする。
  const TABLE_INNER_WIDTH = 1150;
  const ROW_FIXED_PARTS_WIDTH = 404;
  const DEFAULT_ITEM_BOX = 120; // cell幅80 + 左右パディング20 + 右マージン20
  const rankItemBox = rankMode
    ? Math.min(DEFAULT_ITEM_BOX, Math.floor((TABLE_INNER_WIDTH - ROW_FIXED_PARTS_WIDTH) / rankCount))
    : DEFAULT_ITEM_BOX;
  const rankItemBoxDelta = DEFAULT_ITEM_BOX - rankItemBox;
  const rankCellWidth = 80 - rankItemBoxDelta;
  const rankNavGroupWidth = 100 - rankItemBoxDelta;

  // 1. ログイン中ユーザーから学校名を取得
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      // schools.owner は通常は単一uid文字列だが、運営メンバーにもアクセスを許可した
      // 学校だけ配列([元のuid, 追加のuid, ...])になっているため両方にマッチさせる。
      const q = query(
        collection(db, 'schools'),
        or(where('owner', '==', user.uid), where('owner', 'array-contains', user.uid))
      );
      const snap = await getDocs(q);
      if (snap.empty) return;
      setSchoolName(snap.docs[0].data().name ?? '');
    });
    return () => unsubscribe();
  }, []);

  // 2. schoolName 確定後にユーザー + guidance データ取得
  useEffect(() => {
    if (!schoolName) return;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const infoSnap = await getDoc(doc(db, 'info', 'guidance'));
        const criteria = infoSnap.data()?.[schoolName];
        const rc = criteria?.mode === 'rank' ? Number(criteria?.rankCount ?? 0) : 0;
        setRankCount(rc > 0 ? rc : 0);

        const usersSnap = await getDocs(
          query(collection(db, 'users'), where('education.school', '==', schoolName))
        );

        const targetUsers = usersSnap.docs
          .map(d => ({ id: d.id, ...(d.data() as any) }))
          .filter(u => {
            if (!criteria) return true;
            const edu = u.education ?? {};
            const deptOk = !criteria.department?.length || criteria.department.includes(edu.department);
            const gradeOk = !criteria.grade?.length     || criteria.grade.includes(edu.grade);
            const classOk = !criteria.class?.length     || criteria.class.includes(edu.class);
            return deptOk && gradeOk && classOk;
          });

        const rowData: UserRow[] = await Promise.all(
          targetUsers.map(async (u) => {
            const answered = u.activity?.guidance?.request === true;
            const reported = u.activity?.guidance?.report === true;
            let group1: GuidanceEntry[] = [];
            let group2: GuidanceEntry[] = [];
            let ranked: (GuidanceEntry | undefined)[] = [];
            let all: GuidanceEntry[] = [];

            if (answered) {
              const gSnap = await getDocs(collection(db, 'users', u.id, 'guidance'));
              all = gSnap.docs
                .map(d => d.data() as GuidanceEntry)
                .sort((a, b) => (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0));
              group1 = all.filter(e => e.status === '第1志望群').slice(0, 3);
              group2 = all.filter(e => e.status === '第2志望群').slice(0, 3);
              all.forEach(e => {
                const r = rankOf(e.status);
                if (r) ranked[r - 1] = e;
              });
            }

            return { uid: u.id, name: u.education?.name ?? '', class: u.education?.class ?? '', number: u.education?.number ?? '', answered, reported, group1, group2, ranked, all };
          })
        );

        setRows(rowData);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [schoolName]);

  // 2-2. Web のガイダンスの回答(同じ学校名の分だけ)
  useEffect(() => {
    if (!schoolName) return;
    const fetchWeb = async () => {
      setWebLoading(true);
      try {
        const call = httpsCallable<unknown, { rows: WebGuidanceRow[] }>(
          getFunctions(getApp(), 'asia-northeast1'), 'getWebGuidanceAnswers');
        const res = await call({});
        const list: UserRow[] = (res.data.rows ?? []).map((w) => {
          const all = w.entries;
          const ranked: (GuidanceEntry | undefined)[] = [];
          all.forEach(e => {
            const r = rankOf(e.status);
            if (r) ranked[r - 1] = e;
          });
          return {
            uid: `web:${w.id}`,
            name: w.name,
            class: w.className,
            // Web回答(ログインせず回答した分)は出席番号を持たない
            number: '',
            answered: w.answered,
            reported: w.reported,
            group1: all.filter(e => e.status === '第1志望群').slice(0, 3),
            group2: all.filter(e => e.status === '第2志望群').slice(0, 3),
            ranked,
            all,
          };
        });
        setWebRows(list);
      } catch (e) {
        console.error(e);
        setWebRows([]);
      } finally {
        setWebLoading(false);
      }
    };
    fetchWeb();
  }, [schoolName]);

  // 3. モーダルオープン時に各 guidance エントリの searchIndex を取得
  useEffect(() => {
    if (!selectedUid) return;
    const row = [...rows, ...webRows].find(r => r.uid === selectedUid);
    if (!row) return;

    const fetchDetails = async () => {
      setDetailLoading(true);
      const details: Record<string, any> = {};
      for (const entry of row.all) {
        if (!entry.recruitId || details[entry.recruitId]) continue;
        const snap = await getDoc(doc(db, 'searchIndex', entry.recruitId));
        if (snap.exists()) {
          details[entry.recruitId] = snap.data();
        }
      }
      setSalonDetails(details);
      setDetailLoading(false);
    };

    fetchDetails();
  }, [selectedUid]);

  const selectedRow = [...rows, ...webRows].find(r => r.uid === selectedUid) ?? null;

  const getPref = (recruitId: string) => {
    const d = salonDetails[recruitId];
    if (!d) return '';
    return d.storeTags?.find((tag: string) =>
      /^(北海道|東京都|大阪府|京都府|.{2,3}県)$/.test(tag)
    ) ?? d.pref ?? '';
  };

  const renderSalonRow = (entry: GuidanceEntry) => {
    const salon = salonDetails[entry.recruitId];
    return (
      <div key={entry.recruitId} className={Styles.items_table}>
        <div className={Styles.items_table_box_1}>
          <img
            className={Styles.items_table_box_1_img}
            src={`https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/companies_logo%2Fresize_images%2F${entry.companyId}_200x200?alt=media&token=?0`}
            onError={e => { e.currentTarget.src = CompanyDefaultImage; }}
          />
          <div className={Styles.items_table_box_post}>
            {entry.schedule && (
              <div>
                <div />
                <p>{entry.schedule}</p>
              </div>
            )}
            <h2>{rankOf(entry.status) ? `第${rankOf(entry.status)}希望　${entry.name}` : entry.name}</h2>
          </div>
        </div>

        <div className={Styles.items_table_box_2}>
          <h4>{getPref(entry.recruitId)}</h4>
        </div>

        <div style={{ flex: '1', alignItems: 'center' }} />

        <HiOutlineDocumentText
          className={Styles.icon}
          style={salon?.jobPostingsStatus ? {} : { color: '#EBEBEB' }}
          onClick={() => { if (salon?.jobPostingsStatus) setIsJobPosting(entry.recruitId); }}
        />
        <a
          href={salon?.pamphlet ? `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/pamphlet%2F${entry.recruitId}?alt=media&token=` : undefined}
          onClick={e => { if (!salon?.pamphlet) e.preventDefault(); }}
          target="_blank" rel="noopener noreferrer"
        >
          <MdMenuBook className={Styles.icon} style={salon?.pamphlet ? {} : { color: '#EBEBEB' }} />
        </a>
        <a
          href={(salon?.recruitStatus || salon?.jobPostingsStatus) ? `https://memorii-works.com/recruit/id=${entry.recruitId}` : undefined}
          onClick={e => { if (!(salon?.recruitStatus || salon?.jobPostingsStatus)) e.preventDefault(); }}
          target="_blank" rel="noopener noreferrer"
        >
          <HiNewspaper className={Styles.icon} style={(salon?.recruitStatus || salon?.jobPostingsStatus) ? {} : { color: '#EBEBEB' }} />
        </a>
      </div>
    );
  };

  const renderSection = (label: string, entries: GuidanceEntry[], bg: string, color: string = '#1B1B1B') => (
    <div className={Styles.graduations}>
      <p className={Styles.graduation_section_head} style={{ backgroundColor: bg, color }}>{label}</p>
      <div className={Styles.graduation_nav}>
        <h3 className={Styles.graduation_nav_1}>サロン名</h3>
        <h3 className={Styles.graduation_nav_2}>都道府県</h3>
        <h3 className={Styles.graduation_nav_3}>求人票</h3>
        <h3 className={Styles.graduation_nav_3}>パンフ</h3>
        <h3 className={Styles.graduation_nav_3}>求人</h3>
      </div>
      {entries.length === 0
        ? <p className={Styles.modal_empty}>未選択</p>
        : entries.map(renderSalonRow)
      }
    </div>
  );

  // 表のヘッダー(アプリ回答・Web 回答で同じ)。番号・回答はクリックで並び替え(/sanpou と同じ▲▼表示)
  const renderTableNav = () => (
    <div className={Styles.table_nav}>
        <div className={Styles.nav_img} />
        <h3 className={Styles.nav_name}>名前</h3>
        <h3 className={`${Styles.nav_number} ${Styles.sortable}`} onClick={() => toggleSort('number')}>
          番号 {sortConfig.key === 'number' ? (sortConfig.dir === 'asc' ? '▲' : '▼') : '▲▼'}
        </h3>
        <h3 className={`${Styles.nav_status} ${Styles.sortable}`} onClick={() => toggleSort('status')}>
          回答 {sortConfig.key === 'status' ? (sortConfig.dir === 'asc' ? '▲' : '▼') : '▲▼'}
        </h3>
        {rankMode
          ? Array.from({ length: rankCount }, (_, i) => (
              <h3 key={`rh-${i}`} className={Styles.nav_group} style={{ width: rankNavGroupWidth }}>第{i + 1}希望</h3>
            ))
          : <>
              <h3 className={Styles.nav_group}>第1志望群</h3>
              <h3 className={Styles.nav_group}>第1志望群</h3>
              <h3 className={Styles.nav_group}>第1志望群</h3>
              <h3 className={Styles.nav_group}>第2志望群</h3>
              <h3 className={Styles.nav_group}>第2志望群</h3>
              <h3 className={Styles.nav_group}>第2志望群</h3>
            </>}
        <div className={Styles.nav_detail} />
      </div>
  );

  // 表の行(アプリ回答・Web 回答で同じ形)
  const renderRows = (list: UserRow[], isWeb: boolean) => (
    sortRows(list.filter(r => !selectedClass || r.class === selectedClass)).map((row) => (
        <div key={row.uid} data-testid={isWeb ? "guidance-admin-web-row" : "guidance-admin-row"} data-uid={row.uid} className={Styles.row}>
          {/* Web の回答はプロフィール写真が無いので、最初から既定の画像 */}
          <img
            className={Styles.image}
            src={isWeb ? DefaultImage : `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/profiles%2Fresize_images%2F${row.uid}_500x500?alt=media&token=?`}
            onError={e => { e.currentTarget.src = DefaultImage; }}
          />
          <p className={Styles.name} onClick={() => console.log(row.uid)}>{row.name}</p>
          {/* Web回答一覧は出席番号を持たないので空欄のまま */}
          <p className={Styles.number}>{row.number || '-'}</p>
          <p className={Styles.status}
            style={row.reported ? { color: '#D4A017', fontWeight: 700 } : row.answered ? { color: '#2E9E73', fontWeight: 700 } : { color: '#E44E70' }}>
            {row.reported ? '内定済' : row.answered ? '回答済' : '未回答'}
          </p>

          {rankMode && Array.from({ length: rankCount }, (_, i) => (
            <div key={`r-${i}`} className={Styles.cell}
              style={{ width: rankCellWidth, backgroundColor: (!row.reported && row.ranked[i]) ? GROUP1_COLOR : undefined }}>
              <p className={Styles.cell_name}>{row.reported ? '─' : (row.ranked[i]?.name ?? '─')}</p>
              {!row.reported && row.ranked[i]?.schedule && (
                <p className={Styles.cell_schedule}>{row.ranked[i]!.schedule}</p>
              )}
            </div>
          ))}

          {!rankMode && [0, 1, 2].map(i => (
            <div key={`g1-${i}`} className={Styles.cell}
              style={{ backgroundColor: (!row.reported && row.group1[i]) ? GROUP1_COLOR : undefined }}>
              <p className={Styles.cell_name}>{row.reported ? '─' : (row.group1[i]?.name ?? '─')}</p>
              {!row.reported && row.group1[i]?.schedule && (
                <p className={Styles.cell_schedule}>{row.group1[i].schedule}</p>
              )}
            </div>
          ))}

          {!rankMode && [0, 1, 2].map(i => (
            <div key={`g2-${i}`} className={Styles.cell}
              style={{ backgroundColor: (!row.reported && row.group2[i]) ? GROUP2_COLOR : undefined }}>
              <p className={Styles.cell_name}>{row.reported ? '─' : (row.group2[i]?.name ?? '─')}</p>
              {!row.reported && row.group2[i]?.schedule && (
                <p className={Styles.cell_schedule}>{row.group2[i].schedule}</p>
              )}
            </div>
          ))}

          <button
            data-testid="guidance-admin-detail-btn"
            className={Styles.toggle_btn}
            onClick={() => { setSalonDetails({}); setSelectedUid(row.uid); }}
            disabled={!row.answered || row.reported}
            style={(!row.answered || row.reported) ? { color: '#CCCCCC', cursor: 'default', borderColor: '#CCCCCC' } : {}}
          >
            詳細
          </button>
        </div>
      ))
  );

  return (
    <>
    {/* 親(.page)は min-height: 100vh で高さがコンテンツ任せのため、何もしないと
       このブロックの最後の子(Web回答一覧の表)の下マージンが .page を突き抜けて
       外側(.App の白背景)まで響いてしまう。flow-root でブロック整形コンテキストを作り、
       マージンをこのブロックの内側に閉じ込めて、ページの地色(--mw-bg)がきちんと見えるようにする */}
    <div className={Styles.content}>
    <div className={Styles.table}>

      {renderTableNav()}

      {loading && <p className={Styles.message}>読み込み中...</p>}
      {!loading && rows.length === 0 && <p className={Styles.message}>対象の学生がいません。</p>}

      {renderRows(rows, false)}

    </div>

    {/* Web 回答一覧(ログインせずに Web のガイダンスページから回答した学生) */}
    <div className={Styles.table_title}>Web回答一覧</div>
    <div className={Styles.table}>

      {renderTableNav()}

      {webLoading && <p className={Styles.message}>読み込み中...</p>}
      {!webLoading && webRows.length === 0 && <p className={Styles.message}>Web からの回答はまだありません。</p>}

      {renderRows(webRows, true)}

    </div>
    </div>

    {/* 詳細モーダル */}
    {selectedUid !== '' && selectedRow && (
      <div className={Styles.modal}>
        <div className={Styles.modal_bg} onClick={() => setSelectedUid('')} />
        <div className={Styles.modal_contents}>

          <div className={Styles.modal_title}>
            <h4>{selectedRow.name}</h4>
            <RxCross2 data-testid="guidance-admin-modal-close" className={Styles.modal_cross} onClick={() => setSelectedUid('')} />
          </div>

          {detailLoading
            ? <p className={Styles.message}>読み込み中...</p>
            : <>
                {rankMode
                  ? renderSection('志望順位', selectedRow.ranked.filter((e): e is GuidanceEntry => !!e), MODAL_GROUP1_COLOR, MODAL_HEAD_TEXT_COLOR)
                  : <>
                      {renderSection('第1志望群', selectedRow.group1, MODAL_GROUP1_COLOR, MODAL_HEAD_TEXT_COLOR)}
                      {renderSection('第2志望群', selectedRow.group2, MODAL_GROUP2_COLOR, MODAL_HEAD_TEXT_COLOR)}
                    </>}
                {selectedRow.all.filter(e => e.status !== '第1志望群' && e.status !== '第2志望群' && !rankOf(e.status)).length > 0 &&
                  renderSection('未選択', selectedRow.all.filter(e => e.status !== '第1志望群' && e.status !== '第2志望群' && !rankOf(e.status)), '#F5F5F5')
                }
              </>
          }

          <p className={Styles.modal_note}>※現在、非公開の求人票の情報は表示されません。</p>

        </div>
      </div>
    )}

    {isJobPosting !== '' && (
      <SchoolJobPostings setIsJobPosting={setIsJobPosting} recruitId={isJobPosting} />
    )}
    </>
  );
};

export default GuidancePage;
